import { ReactSVG } from "react-svg";
import { useDispatch } from "react-redux";
import React, { useEffect, useState, Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";

import {
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import { convertToYYYYMMDD, formatHealth } from "../../../../helpers/functions";
import { isValidExpiryDate } from "../../../../helpers/input-validations";
import ProvincialCreate from "./ProvincialCreate";
import ThirdPartyCreate from "./ThirdPartyCreate";
import {
  deletePatientPrivateInsuranceDetails,
  deletePatientProvincialInsuranceDetails,
} from "../../../../redux/modules/patient/actions";
import { stateOptions, insurancePlanOptions, orderOfInsuranceOptions } from "../../create/options";
import Pill from "../../../common/Pill";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";
import NewToolTip from "../../../common/New-ToolTip";
import Alert from '../../../common/Alert';

const InsuranceInformation = ({ patient }) => {
  const dispatch = useDispatch();


  const getStateValue = (value) => {
    return stateOptions.find(o => o.value === value)?.label || "";
  }

  const getInsurancePlanName = (value, otherName = "") => {
    return value === "other" ? otherName : insurancePlanOptions.find(o => o.value === value)?.label || "";
  }

  const getOrderOfInsuranceLabel = (value) => {
    return orderOfInsuranceOptions.find(o => o.value === value)?.label || "";
  }

  const renderExpiryDate = (expiryDate) => {
    if (!expiryDate) return "";
    const dateObj = new Date(expiryDate);
    if (isNaN(dateObj.getTime()) || dateObj.getFullYear() <= 1900) return "";
    
    return (
      <>
        {convertToYYYYMMDD(expiryDate)}
        {!isValidExpiryDate(expiryDate) && (
          <Pill type="danger" text="INSURANCE EXPIRED" className="inline-flex mt-1 ml-1 cursor-pointer w-fit" />
        )}
      </>
    );
  };

  const renderEffectiveDate = (effectiveDate) => {
    if (!effectiveDate) return "";
    const dateObj = new Date(effectiveDate);
    if (isNaN(dateObj.getTime()) || dateObj.getFullYear() <= 1900) return "";
    return convertToYYYYMMDD(effectiveDate);
  };

  const [noProvincialInsurance, setNoProvincialInsurance] = useState(!patient.provincialInsurance);
  const [noThirdPartyInsurance, setNoThirdPartyInsurance] = useState(patient.thirdPartyInsurance && patient.thirdPartyInsurance.length < 1);
  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [showCreateSliderInsurance, setShowCreateSliderInsurance] = useState(false);
  const [create, setCreate] = useState(false);
  const [currentInsurance, setCurrentInsurance] = useState(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [enableLeftArrow, setEnableLeftArrow] = useState(false);
  const [enableRightArrow, setEnableRightArrow] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [openDeleteInsurance, setOpenDeleteInsurance] = useState(false);
  const provincialInsuranceOption = patient.provincialInsurance
    ? [
      {
        value: patient.provincialInsurance.id,
        label: getStateValue(patient.provincialInsurance.province),
      },
    ]
    : [];
  const [insuranceValueLabelOptions, setInsuranceValueLabelOptions] = useState([]);


  const deleteInsurance = (id) => {
    dispatch(deletePatientPrivateInsuranceDetails(patient.id, id));
  };

  const handleLeftScroll = () => {
    const element = document.getElementById("insurance-information");
    element.scrollLeft -= 400;
    setEnableRightArrow(
      Math.ceil(element.scrollLeft) + element.clientWidth !== element.scrollWidth
    );
    setEnableLeftArrow(element.scrollLeft !== 0);
  };

  const handleRightScroll = () => {
    const element = document.getElementById("insurance-information");
    element.scrollLeft += 400;
    setEnableRightArrow(
      Math.ceil(element.scrollLeft) + element.clientWidth !== element.scrollWidth
    );
    setEnableLeftArrow(element.scrollLeft !== 0);
  };
  const deleteProvincialInsurance = (id) => {
    dispatch(deletePatientProvincialInsuranceDetails(patient.id, id));
  };

  useEffect(() => {
    if (idToDelete != null) {
      let isProvinceDeleteAction = provincialInsuranceOption.findIndex(o => o.value == idToDelete) >= 0;
      let options = [];

      if (isProvinceDeleteAction) {
        options = options.concat(provincialInsuranceOption);
      } else {
        options = options.concat(
          patient.thirdPartyInsurance.map((option) => ({
            value: option.id,
            label: getInsurancePlanName(option.insurancePlanName, option?.otherInsurancePlanName),
          }))
        );
      }
      setInsuranceValueLabelOptions(options);
    } else {
      setInsuranceValueLabelOptions([]);
    }
  }, [idToDelete])

  useEffect(() => {
    // Access the DOM element using the current property of the ref
    const myElement = document.getElementById("insurance-information");
    if (myElement) {
      setIsOverflowing(myElement.scrollWidth > myElement.clientWidth);
      setEnableRightArrow(true);
    }
    setNoThirdPartyInsurance(patient.thirdPartyInsurance && patient.thirdPartyInsurance.length < 1);
  }, [patient.thirdPartyInsurance, isOverflowing]);

  useEffect(() => {
    setNoProvincialInsurance(!patient.provincialInsurance);
  }, [patient.provincialInsurance]);

  return (
    <div className="w-full relative min-w-[400px]">
      {(
        <ProvincialCreate
          open={showCreateSlider}
          onClose={(value) => {
            setShowCreateSlider(value);
          }}
          patient={patient}
        />
      )}
      {noProvincialInsurance && (
          <Alert
          title="No Provincial Insurance Information"
          type="warning"
          className="mb-4"
          isWrapped={true}
          hideCross={true}
          showTexture={false}
          handleClose={() => setNoProvincialInsurance(false)}
        />
        )}
        {noThirdPartyInsurance && (
          <Alert
          title="No Third Party Insurance Information"
          type="warning"
          className="mb-4"
          isWrapped={true}
          hideCross={true}
          showTexture={false}
          handleClose={() => setNoThirdPartyInsurance(false)}
        />
        )}
      <div
        className="grid grid-flow-col auto-cols-[450px] gap-[30px] whitespace-nowrap overflow-auto h-fit min-h-[550px] scroll-smooth"
        id="insurance-information"
      >
        {!patient.provincialInsurance ? (
          <button
            onClick={() => {
              setShowCreateSlider(true);
            }}
            className="inline-flex justify-center items-center flex-col !w-[450px] flex-none h-full p-4 align-top bg-white border border-dashed rounded border-REELTeal min-h-[550px] text-lg font-medium text-REELTeal"
          >
            <PlusIcon className="w-6 h-6 text-REELTeal" />
            <span className="font-bold text-REELTeal text-body-md mt-2">Add Provincial Insurance</span>
          </button>
        ) : (
          <div className="relative block !w-[450px] flex-none align-top border border-gray-100 rounded bg-gray-10 h-full min-h-[550px] flex flex-col">
            <div className="flex items-center justify-between h-[72px] px-6 py-3 border-b border-gray-100 bg-gray-20 flex-shrink-0">
              <h4 className="flex items-center text-lg font-bold text-teal-600">
                Provincial Insurance
              </h4>

              <div>
                <Menu as="div" className="relative inline-block text-left">
                  <div>
                    <Menu.Button>
                      <ReactSVG
                        src={`/icons/triple-dot.svg`}
                        className={`flex text-black !items-center w-6 scale-x-[1.4] scale-y-[1.4]`}
                      />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform translate-y-2 opacity-0 scale-95"
                    enterTo="transform translate-y-0 opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform translate-y-0 opacity-100 scale-100"
                    leaveTo="transform translate-y-2 opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute top-full left-1/2 -translate-x-1/2 mt-1 min-w-[80px] w-auto origin-top bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                      <Menu.Item>
                        <button
                          className="block text-left w-[80px] h-[38px] px-[10px] py-[10px] flex items-center gap-[10px] border-b border-b-neutral-70 hover:bg-gray-50"
                          onClick={() => {
                            setShowCreateSlider(true);
                          }}
                        >
                          Edit
                        </button>
                      </Menu.Item>
                      <Menu.Item>
                        <button
                          className="block text-left w-[80px] h-[38px] px-[10px] py-[10px] flex items-center gap-[10px] border-b border-b-neutral-70 hover:bg-gray-50"
                          onClick={() => {
                            setIdToDelete(patient.provincialInsurance.id);
                            setOpenDeleteInsurance(true);
                          }}
                        >
                          Delete
                        </button>
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
            <div className="px-6 py-3.5 flex flex-col h-full">
              <>
                <div className="flex-1 overflow-y-auto pr-2">
                  <div className="grid grid-cols-1">
                    <div>
                      <p className="mb-1.5 text-sm text-gray-700">Province</p>
                      <p className="font-bold text-gray-800 capitalize text-body-md">
                        {stateOptions.find(
                          (option) =>
                            option.value === patient.provincialInsurance?.province
                        )?.label || ""}
                      </p>
                    </div>
                    <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                    <div>
                      <p className="mb-1.5 text-sm text-gray-700">Health Card Number</p>
                      <p className="font-bold text-gray-800 text-body-md">
                        {patient.provincialInsurance?.cardNumber ? formatHealth(patient.provincialInsurance.cardNumber) : ""}
                      </p>
                    </div>
                    <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                    <div>
                      <p className="mb-1.5 text-sm text-gray-700">Version Code</p>
                      <p className="font-bold text-gray-800 text-body-md">
                        {patient.provincialInsurance?.versionCode?.toUpperCase() || ""}
                      </p>
                    </div>
                    <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                    <div>
                      <p className="mb-1.5 text-sm text-gray-700">Expiry date</p>
                      <p className="font-bold text-gray-800 text-body-md">
                        {renderExpiryDate(patient?.provincialInsurance?.expiryDate)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 border-t border-gray-200 pt-3 mt-3">
                  <div className="col-span-full">
                    <p className="mb-1.5 text-sm text-gray-700">Comments</p>
                    <p className="text-body-md font-bold text-gray-800 capitalize min-h-[22px]">
                      {patient.provincialInsurance?.comments || ""}
                    </p>
                  </div>
                </div>
              </>
            </div>
          </div>
        )}
        {patient.thirdPartyInsurance.length < 1 && (
          <button
            onClick={() => {
              setShowCreateSliderInsurance(true);
              setCreate(true);
            }}
            className="inline-flex justify-center items-center flex-col !w-[450px] flex-none h-full p-4 align-top bg-white border border-dashed rounded border-REELTeal min-h-[550px] text-lg font-medium text-REELTeal"
          >
            <PlusIcon className="w-6 h-6 text-REELTeal" />
            <span className="font-bold text-REELTeal text-body-md">Add Third Party Insurance</span>
          </button>
        )}
        {patient.thirdPartyInsurance.map((item, i) => (
          <div
            key={i}
            className="relative inline-block !w-[450px] flex-none h-full min-h-[550px] align-top border border-gray-100 rounded bg-gray-10 flex flex-col"
          >
            {!create && (
              <ThirdPartyCreate
                open={currentInsurance === item.id && showCreateSliderInsurance}
                onClose={(value) => {
                  setShowCreateSliderInsurance(value);
                }}
                patient={patient}
                insurance={item}
                create={create}
              />
            )}
            <div className="flex items-center justify-between h-[72px] px-6 py-3 border-b border-gray-100 bg-gray-20 flex-shrink-0">
              <h4 className="text-lg font-bold text-teal-600">Third Party Insurance</h4>
              <div>
                <Menu as="div" className="relative inline-block text-left">
                  <div>
                    <Menu.Button>
                      <ReactSVG
                        src={`/icons/triple-dot.svg`}
                        className={`flex text-black !items-center w-6 scale-x-[1.4] scale-y-[1.4]`}
                      />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform translate-y-2 opacity-0 scale-95"
                    enterTo="transform translate-y-0 opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform translate-y-0 opacity-100 scale-100"
                    leaveTo="transform translate-y-2 opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute top-full left-1/2 -translate-x-1/2 mt-1 min-w-[80px] w-auto origin-top bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                      <Menu.Item>
                        <button
                          className="block text-left w-[80px] h-[38px] px-[10px] py-[10px] flex items-center gap-[10px] border-b border-b-neutral-70 hover:bg-gray-50"
                          onClick={() => {
                            setShowCreateSliderInsurance(true);
                            setCreate(false);
                            setCurrentInsurance(item.id);
                          }}
                        >
                          Edit
                        </button>
                      </Menu.Item>
                      <Menu.Item>
                        <button
                          className="block text-left w-[80px] h-[38px] px-[10px] py-[10px] flex items-center gap-[10px] border-b border-b-neutral-70 hover:bg-gray-50"
                          onClick={() => {
                            setIdToDelete(item.id);
                            setOpenDeleteInsurance(true);
                          }}
                        >
                          Delete
                        </button>
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
              {/* <div className="flex items-center space-x-4">
                <button
                  onClick={() => {
                    setShowCreateSliderInsurance(true);
                    setCreate(false);
                    setCurrentInsurance(item.id);
                  }}
                >
                  <PencilSquareIcon className="w-5 h-5 text-teal-600" />
                </button>
                <button onClick={() => deleteInsurance(item.id)}>
                  <TrashIcon className="w-5 h-5 text-red-300" />
                </button>
              </div> */}
            </div>
            <div className="px-6 py-3.5 flex flex-col h-full">
              <div className="flex-1 overflow-y-auto pr-2">
                <div className="grid grid-cols-1">
                  <div>
                    <p className="mb-1.5 text-sm text-gray-700">Insurance Plan Name</p>
                    <p className="font-bold text-gray-800 capitalize text-body-md">
                      {getInsurancePlanName(item.insurancePlanName, item.otherInsurancePlanName) || ""}
                    </p>
                  </div>
                  <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                  {item.insurancePlanName === "other" && (
                    <>
                      <div>
                        <p className="mb-1.5 text-sm text-gray-700">Other (Insurance Plan Name)</p>
                        <p className="font-bold text-gray-800 capitalize text-body-md">
                          {item.otherInsurancePlanName || ""}
                        </p>
                      </div>
                      <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                    </>
                  )}
                  <div>
                    <p className="mb-1.5 text-sm text-gray-700">Order of Insurance</p>
                    <p className="font-bold text-gray-800 capitalize text-body-md">
                      {getOrderOfInsuranceLabel(item.orderOfInsurance) || ""}
                    </p>
                  </div>
                  <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                  <div>
                    <p
                      className="mb-1.5 text-sm text-gray-700 relative flex gap-2 "
                      data-tooltip-id={`insuranceStatusInformation-${item.id}`}
                    >
                      Plan Status
                      <ReactSVG
                        src="/icons/information-dark.svg"
                        className="w-3.5 pt-0.5"
                      />
                      <NewToolTip
                        id={`insuranceStatusInformation-${item.id}`}
                        render={() => (
                          <p className="max-w-[460px] p-2 text-teal-300 bg-white text-body-sm">
                            Mark Inactive if the plan is no longer ACTIVE.
                            <br/>
                            An alternative to inactivating a plan is to delete the Insurance Plan.
                          </p>
                        )}
                      />
                    </p>
                    <p className="font-bold text-gray-800 capitalize text-body-md">
                      {item.status || ""}
                    </p>
                  </div>
                  <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                  <div>
                    <p className="mb-1.5 text-sm text-gray-700">Effective Date</p>
                    <p className="font-bold text-gray-800 text-body-md">
                      {renderEffectiveDate(item.effectiveDate)}
                    </p>
                  </div>
                  <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                  <div>
                    <p className="mb-1.5 text-sm text-gray-700">Expiry Date</p>
                    <p className="font-bold text-gray-800 text-body-md">
                      {renderExpiryDate(item.expiryDate)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 border-t border-gray-200 pt-3 mt-3">
                <div className="col-span-full">
                  <p className="mb-1.5 text-sm text-gray-700">Comments</p>
                  <p className="text-body-md font-bold text-gray-800 capitalize min-h-[22px]">
                    {item.comments || ""}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
        {patient.thirdPartyInsurance.length > 0 && (
          <button
            onClick={() => {
              setShowCreateSliderInsurance(true);
              setCreate(true);
            }}
            className="inline-flex justify-center items-center flex-col !w-[450px] flex-none h-full p-4 align-top bg-white border border-dashed rounded border-REELTeal min-h-[230px] text-lg font-medium text-REELTeal"
          >
            <PlusIcon className="w-6 h-6 text-REELTeal" />
            <span className="font-bold text-REELTeal text-body-md mt-2">Add Third Party Insurance</span>
          </button>
        )}
        {create && (
          <ThirdPartyCreate
            open={showCreateSliderInsurance}
            onClose={(value) => {
              setShowCreateSliderInsurance(value);
            }}
            patient={patient}
            create={create}
          />
        )}
      </div>

      {isOverflowing && (
        <>
          {enableLeftArrow && (
            <button
              className="absolute m-auto left-0 bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
              onClick={handleLeftScroll}
            >
              <ReactSVG
                src="/icons/arrow-right.svg"
                className="w-[15px] rotate-180"
              />
            </button>
          )}
          {enableRightArrow && (
            <button
              className="absolute m-auto right-0 bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
              onClick={handleRightScroll}
            >
              <ReactSVG src="/icons/arrow-right.svg" className="w-[15px]" />
            </button>
          )}
        </>
      )}

      <ConfirmationModalNew
        type="primary"
        title={`Delete ${patient.provincialInsurance?.id == idToDelete ? 'Provincial ' : 'Third Party '}Insurance`}
        content={() => "Are you sure you want to delete this insurance?"}
        confirmAction={() => {
          if (patient.provincialInsurance?.id === idToDelete)
            deleteProvincialInsurance(idToDelete);
          else deleteInsurance(idToDelete);
          setIdToDelete(null);
          setOpenDeleteInsurance(false);
        }}
        open={openDeleteInsurance}
        handleClose={() => setOpenDeleteInsurance(false)}
        onCross={() => setOpenDeleteInsurance(false)}
        options={insuranceValueLabelOptions}
        selectedOption={idToDelete}
        confirmText="Delete"
      />
    </div>
  );
};

export default InsuranceInformation;
